/**
 * SMS add-on pricing, consent copy and entitlement checks, shared by the
 * SubscriptionCard and the Settings page's add-on card so the price and the
 * legally required purchase disclosure live in one place.
 */

/**
 * Key the backend subscription payload uses to report SMS add-on entitlement.
 */
export const SMS_ADDON_FLAG_KEY = 'has_sms_addon';

/**
 * True when the given payload actually carries the SMS add-on flag.
 * Presence, not truthiness: a payload without the key comes from a backend that
 * does not report - and therefore cannot sell - the add-on, which is a different
 * state from a backend that reports the add-on as off.
 */
export const reportsSmsAddonFlag = (payload) =>
	Boolean(payload) &&
	Object.prototype.hasOwnProperty.call(payload, SMS_ADDON_FLAG_KEY);

/**
 * Recurring list price of the SMS add-on, as presented to the buyer.
 */
export const SMS_ADDON_MONTHLY_PRICE = '$19.99';

/**
 * Recurring SMS add-on cost for one billing period, in dollars.
 */
export const getSmsAddonPeriodCost = (isYearly) => (isYearly ? 19.99 * 12 : 19.99);

/**
 * Consent copy for the standalone SMS add-on purchase.
 * Confirming adds the Stripe subscription item with proration enabled and the
 * backend immediately invoices and pays the resulting balance off-session, so
 * this dialog is the only place the buyer can learn that a charge lands today.
 * It must therefore disclose both the prorated same-day charge and the
 * recurring price, and that the same-day amount varies with the position in
 * the current billing cycle.
 */
export const SMS_ADDON_PURCHASE_CONSENT_MESSAGE =
	'Enable real-time SMS alerts and 2-way reply resolution for your portfolio ' +
	`at ${SMS_ADDON_MONTHLY_PRICE}/month recurring. Confirming charges you a ` +
	'prorated amount today for the remainder of your current billing period, ' +
	'in addition to the recurring price. The amount charged today depends on ' +
	'how far you are into the current billing cycle, and the full ' +
	`${SMS_ADDON_MONTHLY_PRICE}/month is billed on each renewal after that.`;

/**
 * Confirm-button label for the standalone SMS add-on purchase. It names the
 * same-day prorated charge next to the recurring price so the commit action
 * itself does not read as recurring-only.
 */
export const SMS_ADDON_PURCHASE_CONFIRM_LABEL = `Yes, Add SMS (${SMS_ADDON_MONTHLY_PRICE}/mo + prorated charge today)`;

/**
 * Add-on state for a current-subscription payload.
 * Tri-state: the flag is absent, present and false, or present and true.
 * Absent means the backend does not report the add-on at all, so neither the
 * purchase surface nor the entitlement may be inferred from it.
 *
 * @param {object} currentSubscription state.subscription.currentSubscription
 * @returns {{backendReportsSmsAddon: boolean, hasSmsAddon: boolean}}
 */
export const getSmsAddonState = (currentSubscription) => {
	const subscription = currentSubscription?.subscription || currentSubscription;
	const backendReportsSmsAddon =
		reportsSmsAddonFlag(currentSubscription) ||
		reportsSmsAddonFlag(currentSubscription?.subscription);
	const hasSmsAddon =
		backendReportsSmsAddon &&
		Boolean(
			currentSubscription?.[SMS_ADDON_FLAG_KEY] ||
				subscription?.[SMS_ADDON_FLAG_KEY]
		);
	return { backendReportsSmsAddon, hasSmsAddon };
};
